import { PageHero } from '@components/common';
import { MapPin, Video, Users, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

const locations = [
  { name: 'London', path: '/gb/london', region: 'England' },
  { name: 'Bristol', path: '/gb/bristol', region: 'England' },
  { name: 'Birmingham', path: '/gb/birmingham', region: 'England' },
  { name: 'Manchester', path: '/gb/manchester', region: 'England' },
  { name: 'Leeds', path: '/gb/leeds', region: 'England' },
  { name: 'Liverpool', path: '/gb/liverpool', region: 'England' },
  { name: 'Newcastle', path: '/gb/newcastle', region: 'England' },
  { name: 'Cambridge', path: '/gb/cambridge', region: 'England' },
  { name: 'Oxford', path: '/gb/oxford', region: 'England' },
  { name: 'Reading', path: '/gb/reading', region: 'England' },
  { name: 'Brighton', path: '/gb/brighton', region: 'England' },
  { name: 'Exeter', path: '/gb/exeter', region: 'England' },
  { name: 'Cardiff', path: '/gb/cardiff', region: 'Wales' },
  { name: 'Edinburgh', path: '/gb/edinburgh', region: 'Scotland' },
  { name: 'Glasgow', path: '/gb/glasgow', region: 'Scotland' },
  { name: 'Belfast', path: '/gb/belfast', region: 'Northern Ireland' },
];

// City marker positions (percentages based on new SVG map)
const cityMarkers = [
  { name: 'London', left: '80%', top: '75%', size: 'lg' },
  { name: 'Manchester', left: '66%', top: '62%', size: 'md' },
  { name: 'Birmingham', left: '67%', top: '70%', size: 'md' },
  { name: 'Edinburgh', left: '58%', top: '44.5%', size: 'md' },
  { name: 'Glasgow', left: '51%', top: '45%', size: 'md' },
  { name: 'Cardiff', left: '58%', top: '75%', size: 'md' },
  { name: 'Belfast', left: '37.5%', top: '53.5%', size: 'md' },
  { name: 'Bristol', left: '63%', top: '76%', size: 'sm' },
  { name: 'Leeds', left: '70.5%', top: '59.5%', size: 'sm' },
  { name: 'Liverpool', left: '61%', top: '63%', size: 'sm' },
  { name: 'Newcastle', left: '69.5%', top: '51%', size: 'sm' },
  { name: 'Cambridge', left: '82.5%', top: '71%', size: 'xs' },
  { name: 'Oxford', left: '72%', top: '74%', size: 'xs' },
  { name: 'Brighton', left: '81.5%', top: '80.5%', size: 'xs' },
  { name: 'Reading', left: '74.5%', top: '76%', size: 'xs' },
  { name: 'Exeter', left: '58%', top: '80%', size: 'xs' },
];

export default function LocationsPage() {
  // LocalBusiness structured data for SEO
  const locationsStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'The CISO Network',
    description: 'Fractional CISO services across the UK',
    url: 'https://thecisonetwork.com',
    areaServed: locations.map(loc => ({
      '@type': 'City',
      name: loc.name,
    })),
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'GB',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '54.5',
      longitude: '-3.5',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(locationsStructuredData) }}
      />
      <PageHero
        title="Fractional CISO Services Across the UK"
        subtitle="Remote-first security leadership, available wherever you operate"
        eyebrow="Locations"
        seoTitle="Fractional CISO Services UK | The CISO Network"
        seoDescription="Expert fractional CISO services available remotely across the UK. Serving London, Manchester, Birmingham, Edinburgh, Cardiff, Belfast and more."
      />

      {/* How We Work Remotely */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
              Security Leadership Without the Commute
            </h2>
            <p className="text-lg text-muted-foreground">
              Our fractional CISOs operate on a remote-first basis, providing the same strategic
              security leadership as an in-house executive - just without the overhead and
              geographic constraints.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="p-6 rounded-xl bg-background border border-border">
              <div className="w-12 h-12 rounded-lg bg-teal/10 flex items-center justify-center mb-4">
                <Video className="h-6 w-6 text-teal" />
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                Virtual-First Engagement
              </h3>
              <p className="text-muted-foreground text-sm">
                Regular video calls, instant messaging, and collaborative tools keep us connected to
                your team. Board presentations, incident response, and day-to-day guidance - all
                delivered remotely.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-background border border-border">
              <div className="w-12 h-12 rounded-lg bg-teal/10 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-teal" />
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                On-Site When It Matters
              </h3>
              <p className="text-muted-foreground text-sm">
                While day-to-day operations run remotely, we're available for critical on-site
                presence: board meetings, audit preparation, incident response, and team workshops
                across the UK.
              </p>
            </div>

            <div className="p-6 rounded-xl bg-background border border-border">
              <div className="w-12 h-12 rounded-lg bg-teal/10 flex items-center justify-center mb-4">
                <Globe className="h-6 w-6 text-teal" />
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                Time Zone Aligned
              </h3>
              <p className="text-muted-foreground text-sm">
                UK-based CISOs working your business hours. No language barriers, no time zone
                challenges - just immediate availability when security decisions need to be made.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* UK Map Section */}
      <section className="py-24 bg-muted">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
              Serving Businesses Across the UK
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* UK Map with markers */}
            <div className="bg-white rounded-2xl p-6 border border-border">
              <div className="relative max-w-md mx-auto">
                <img
                  src="/images/uk-map.svg"
                  alt="Map of UK showing CISO service locations"
                  className="w-full h-auto"
                  style={{ filter: 'hue-rotate(-182deg) saturate(0.5)' }}
                />

                {/* City markers overlaid on the map */}
                <div className="absolute inset-0">
                  {cityMarkers.map(city => {
                    const slug = city.name.toLowerCase();
                    const sizeClasses = {
                      lg: 'w-4 h-4',
                      md: 'w-3 h-3',
                      sm: 'w-2.5 h-2.5',
                      xs: 'w-2 h-2',
                    }[city.size];

                    return (
                      <Link
                        key={city.name}
                        to={`/gb/${slug}`}
                        className="absolute group"
                        style={{ left: city.left, top: city.top }}
                      >
                        <span
                          className={`block ${sizeClasses} rounded-full bg-teal border-2 border-white shadow-md group-hover:scale-125 transition-transform`}
                        />
                        <span className="absolute left-4 top-0 text-xs font-medium text-foreground whitespace-nowrap bg-white/90 px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity -translate-y-1/2">
                          {city.name}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Location List */}
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-teal" />
                  England
                </h3>
                <div className="grid grid-cols-2 gap-x-12 gap-y-2 pl-4">
                  {locations
                    .filter(l => l.region === 'England')
                    .map(location => (
                      <Link
                        key={location.path}
                        to={location.path}
                        className="text-sm text-muted-foreground hover:text-teal transition-colors py-1"
                      >
                        {location.name}
                      </Link>
                    ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-teal" />
                  Scotland
                </h3>
                <div className="grid grid-cols-2 gap-x-12 gap-y-2 pl-4">
                  {locations
                    .filter(l => l.region === 'Scotland')
                    .map(location => (
                      <Link
                        key={location.path}
                        to={location.path}
                        className="text-sm text-muted-foreground hover:text-teal transition-colors py-1"
                      >
                        {location.name}
                      </Link>
                    ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-teal" />
                    Wales
                  </h3>
                  <div className="space-y-2 pl-4">
                    {locations
                      .filter(l => l.region === 'Wales')
                      .map(location => (
                        <Link
                          key={location.path}
                          to={location.path}
                          className="block text-sm text-muted-foreground hover:text-teal transition-colors py-1"
                        >
                          {location.name}
                        </Link>
                      ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-teal" />
                    Northern Ireland
                  </h3>
                  <div className="space-y-2 pl-4">
                    {locations
                      .filter(l => l.region === 'Northern Ireland')
                      .map(location => (
                        <Link
                          key={location.path}
                          to={location.path}
                          className="block text-sm text-muted-foreground hover:text-teal transition-colors py-1"
                        >
                          {location.name}
                        </Link>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-primary">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Wherever You Are in the UK
          </h2>
          <p className="text-lg text-primary-foreground/70 mb-8 max-w-2xl mx-auto">
            Top tier security leadership is available to your business - whether you're in a tech
            hub like London or Bristol, or operating from anywhere else in the UK.
          </p>
          <a
            href="https://calendly.com/thecisonetwork/15min"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-hero text-lg px-10 py-4"
          >
            Book a Call
          </a>
        </div>
      </section>

      {/* Global Reach CTA */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
              UK-Focused, Globally Connected
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              Our client work is largely in the UK, but our network of experienced CISOs extends
              well beyond it. If you are based outside the UK, we would still encourage you to get
              in touch; we may be able to help directly, or point you toward trusted fractional CISO
              contacts in your territory.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
